import React, { useEffect, useState } from "react";
import { PROMO_FEATURES_ENABLED } from "../config/promoConfig";

export const Navigation = (props) => {
  const [dynamicPromoEnabled, setDynamicPromoEnabled] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://yuccan.tech';

  // Fetch current promo state from API
  useEffect(() => {
    const fetchPromoState = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/static-config/promo`);
        const result = await response.json();
        if (result.success) {
          setDynamicPromoEnabled(result.data.enabled);
        }
      } catch (err) {
        console.warn('Navigation: Failed to fetch promo config, using static fallback');
      }
    };
    
    fetchPromoState();
    
    // Poll every 5 seconds to check for changes
    const interval = setInterval(fetchPromoState, 5000);
    return () => clearInterval(interval);
  }, [API_BASE_URL]);

  useEffect(() => {
    // Only apply these modifications on mobile to avoid crazy high navbar on desktop
    if (window.innerWidth <= 768) {
      // Set navigation bar padding and fix internal elements
      const menu = document.getElementById('menu');
      if (menu) {
        menu.style.transition = 'none'; // Disable ALL CSS transitions
        menu.style.padding = '0px 15px 0px 15px !important'; // Force with !important
        menu.style.paddingBottom = '0px !important'; // Double-specify bottom padding
        menu.style.boxShadow = 'none';
        menu.style.border = 'none';
        menu.style.borderBottom = 'none';
        menu.style.minHeight = 'auto';
        menu.style.height = 'auto';
        
        // Check what might be overriding - target the container too
        const container = menu.querySelector('.container');
        if (container) {
          container.style.paddingBottom = '0px';
          container.style.marginBottom = '0px';
        }
        
        // Target navbar-header which might have its own padding
        const navbarHeader = menu.querySelector('.navbar-header');
        if (navbarHeader) {
          navbarHeader.style.paddingBottom = '0px';
          navbarHeader.style.marginBottom = '0px';
        }
        
        // Disable transitions on all child elements too
        const allChildren = menu.querySelectorAll('*');
        allChildren.forEach(child => {
          child.style.transition = 'none';
        });
        
        // Also fix the navbar brand padding
        const navbarBrand = menu.querySelector('.navbar-brand');
        if (navbarBrand) {
          navbarBrand.style.padding = '0px 15px 0px 15px'; // Reduce bottom padding here too
          navbarBrand.style.margin = '0';
          navbarBrand.style.transition = 'none';
        }
      }
    }

    // MOBILE ONLY: Override existing CSS mobile rules (lines 284-322 in style.css)
    if (window.innerWidth <= 768) {
      const menu = document.getElementById('menu');
      if (menu) {
        // Override existing mobile padding: was "5px 15px", make it "0px 15px"
        menu.style.padding = '0px 15px !important';
        
        // The existing CSS uses specific selectors: body #menu.navbar-fixed-top
        // So I need to ensure the menu has the .navbar-fixed-top class
        if (!menu.classList.contains('navbar-fixed-top')) {
          menu.classList.add('navbar-fixed-top');
        }
        
        // Now the existing CSS rules should work:
        // - Lines 291-297: navbar-header centering
        // - Lines 300-307: navbar-brand centering  
        // - Lines 312-322: navbar-toggle centering
        
        // But if they still don't work, force with higher specificity
        const navbarHeader = menu.querySelector('.navbar-header');
        const navbarBrand = menu.querySelector('.navbar-brand');
        const navbarToggle = menu.querySelector('.navbar-toggle');
        
        if (navbarHeader) {
          navbarHeader.style.cssText += 'display: flex !important; flex-direction: column !important; align-items: center !important; float: none !important; width: auto !important;';
        }
        
        if (navbarBrand) {
          navbarBrand.style.cssText += 'display: block !important; float: none !important; margin: 0 auto !important; padding: 0 !important; text-align: center !important; order: 1 !important;';
        }
        
        if (navbarToggle) {
          navbarToggle.style.cssText += 'display: block !important; margin: 10px auto 0 auto !important; order: 2 !important; position: relative !important;';
        }
      }
    }

    // MOBILE ONLY: The logical solution - completely disable the problematic CSS
    // The issue is CSS lines 365-389 that applies full-width to specific sections but NOT roi-calculator
    if (window.innerWidth <= 768) {
      // Create a <style> element to override the problematic CSS completely
      let overrideStyle = document.getElementById('mobile-section-override');
      if (!overrideStyle) {
        overrideStyle = document.createElement('style');
        overrideStyle.id = 'mobile-section-override';
        overrideStyle.innerHTML = `
          @media (max-width: 768px) {
            #about, #services, #portfolio, #testimonials, #contact, #promos, #footer, #solutions {
              width: auto !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              box-sizing: content-box !important;
              overflow-x: visible !important;
            }
          }
        `;
        document.head.appendChild(overrideStyle);
      }
    }

    // MOBILE ONLY: Copy Telematics Hero mobile arrangement to Process page
    // Note: Remote Assets already uses .telematics-hero so it already has the mobile styling
    if (window.innerWidth <= 576) {
      // Create CSS to apply Telematics mobile hero styling to Process page only
      let heroMobileStyle = document.getElementById('hero-mobile-override');
      if (!heroMobileStyle) {
        heroMobileStyle = document.createElement('style');
        heroMobileStyle.id = 'hero-mobile-override';
        heroMobileStyle.innerHTML = `
          @media (max-width: 576px) {
            .process-hero {
              height: 40vh !important;
              min-height: 300px !important;
            }
            
            /* Hide the overlay within the hero - same as telematics */
            .process-hero .hero-overlay {
              display: none !important;
            }
          }
        `;
        document.head.appendChild(heroMobileStyle);
      }
    }
  }, []);
  // Auto-collapse menu when navigation links are clicked
  const handleLinkClick = (e) => {
    const navbarCollapse = document.getElementById('bs-example-navbar-collapse-1');
    const navbarToggle = document.querySelector('.navbar-toggle');
    
    if (navbarCollapse && navbarCollapse.classList.contains('in')) {
      navbarCollapse.classList.remove('in');
      if (navbarToggle) {
        navbarToggle.classList.add('collapsed');
      }
    }
    
    // On mobile, override smooth scroll with promotional banner logic
    const isMobile = window.innerWidth <= 768;
    if (isMobile && e.target.hash && (dynamicPromoEnabled !== null ? dynamicPromoEnabled : PROMO_FEATURES_ENABLED)) {
      e.preventDefault();
      const targetElement = document.querySelector(e.target.hash);
      if (targetElement) {
        const menuBar = document.getElementById('menu');
        const menuHeight = menuBar ? menuBar.offsetHeight : 80;
        const isPromoActive = dynamicPromoEnabled !== null ? dynamicPromoEnabled : PROMO_FEATURES_ENABLED;
        const promotionalBannerHeight = isPromoActive ? 50 : 0;

        // Special case for logo link to page-top - scroll to very top (0)
        if (e.target.hash === '#page-top') {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        } else {
          // Other sections use offset calculation
          const totalOffset = menuHeight + promotionalBannerHeight;
          const targetPosition = targetElement.offsetTop - totalOffset;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  // Listen for promo state changes from promotional banner component (fallback sync)
  useEffect(() => {
    const handlePromoStateChange = (event) => {
      setDynamicPromoEnabled(event.detail.enabled);
    };

    const handleStorageChange = () => {
      const storedPromoEnabled = localStorage.getItem('promoEnabled');
      if (storedPromoEnabled !== null) {
        setDynamicPromoEnabled(storedPromoEnabled === 'true');
      }
    };

    // Listen for custom events and storage changes
    window.addEventListener('promoStateChange', handlePromoStateChange);
    window.addEventListener('storage', handleStorageChange);
    
    // Check localStorage on component mount
    handleStorageChange();

    return () => {
      window.removeEventListener('promoStateChange', handlePromoStateChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <nav id="menu" className={`navbar navbar-default navbar-fixed-top ${(dynamicPromoEnabled !== null ? dynamicPromoEnabled : PROMO_FEATURES_ENABLED) ? 'with-promo-banner' : 'no-promo-banner'}`}>
      <div className="container">
        <div className="navbar-header">
          <button
            type="button"
            className="navbar-toggle collapsed"
            data-toggle="collapse"
            data-target="#bs-example-navbar-collapse-1"
          >
          </button>
          <a className="navbar-brand page-scroll" href="#page-top" onClick={(e) => {
            // Only apply mobile scrolling behavior on mobile devices
            const isMobile = window.innerWidth <= 768;
            if (isMobile) {
              e.preventDefault();
              handleLinkClick(e);
              // Scroll to top for mobile
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            // On desktop, default behavior remains unchanged
          }}>
            <img 
              src="/img/Banner_16x_TR_Trim&ColorEdit.png" 
              alt="Yuccan Technologies Logo" 
              style={{height: '60px', width: 'auto'}}
            />
          </a>
        </div>

        <div
          className="collapse navbar-collapse"
          id="bs-example-navbar-collapse-1"
        >
          <ul className="nav navbar-nav navbar-right">
            <li className="dropdown">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" className="dropdown-toggle" data-toggle="dropdown">
                Our Solutions <span className="caret"></span>
              </a>
              <ul className="dropdown-menu">
                <li><a href="#solutions" className="page-scroll" onClick={handleLinkClick}>All Solutions</a></li>
                <li><a href="/telematics" target="_blank" rel="noopener noreferrer">Mobile Asset Telematics</a></li>
                <li><a href="/dynamic-pricing" target="_blank" rel="noopener noreferrer">Dynamic Product Pricing</a></li>
                <li><a href="/remote-assets" target="_blank" rel="noopener noreferrer">Remote Asset Monitoring & Control</a></li>
              </ul>
            </li>
            <li>
              <a href="#portfolio" className="page-scroll" onClick={handleLinkClick}>
                Portfolio
              </a>
            </li>
            <li>
              <a href="#roi-calculator" className="page-scroll" onClick={handleLinkClick}>
                Waste Estimator
              </a>
            </li>
            <li>
              <a href="#contact" className="page-scroll" onClick={handleLinkClick}>
                Get Started
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};
